import { useState, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../services/supabase';
import { useAuth } from '../context/AuthContext';
import { Transition, Dialog } from '@headlessui/react';
import { Fragment } from 'react';
import { toast } from 'react-toastify';
import { Link } from 'react-router-dom';
import { PencilIcon, TrashIcon, ChevronUpIcon, ChevronDownIcon, DocumentArrowDownIcon, ArrowPathIcon } from '@heroicons/react/20/solid';
import { generatePackingSlipPDF } from '../utils/packingSlipUtils';

function Deliveries() {
  const { user } = useAuth();
  const [deliveries, setDeliveries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [customers, setCustomers] = useState([]);

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCustomer, setFilterCustomer] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);
  const [filterPartNumber, setFilterPartNumber] = useState('');
  const [partNumberQuery, setPartNumberQuery] = useState('');
  const [showPartNumberDropdown, setShowPartNumberDropdown] = useState(false);
  const [dateFilter, setDateFilter] = useState('last30days');
  const [showDateDropdown, setShowDateDropdown] = useState(false);
  const [showUninvoiced, setShowUninvoiced] = useState(true);
  const partNumberDropdownRef = useRef(null);

  // Sorting
  const [sortField, setSortField] = useState('ship_date');
  const [sortDirection, setSortDirection] = useState('desc');

  // Modals
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isLineItemModalOpen, setIsLineItemModalOpen] = useState(false);
  const [selectedDelivery, setSelectedDelivery] = useState(null);
  const [selectedPackage, setSelectedPackage] = useState(null);

  // Edit form state
  const [editInvoiceNumber, setEditInvoiceNumber] = useState('');
  const [editTrackingNumber, setEditTrackingNumber] = useState('');
  const [editShipDate, setEditShipDate] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [editQuantity, setEditQuantity] = useState('');
  const [invoiceEntirePackage, setInvoiceEntirePackage] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);

  document.title = 'Deliveries';

  const dateFilterOptions = [
    { value: 'last7days', label: 'Last 7 Days' },
    { value: 'last30days', label: 'Last 30 Days' },
    { value: 'last3months', label: 'Last 3 Months' },
    { value: 'thismonth', label: 'This Month' },
    { value: 'all', label: 'All Time' },
  ];

  const formatDateToString = (date) => date.toISOString().split('T')[0];

  useEffect(() => {
    const fetchDeliveries = async () => {
      if (!user) return;

      setLoading(true);
      try {
        const [deliveriesResult, customersResult] = await Promise.all([
          supabase
            .from('deliveries')
            .select(`
              delivery_id, package_number, po_number, part_number, quantity, ship_date, invoice_number, tracking_number, notes, packing_slip_path,
              job_id, kanban_job_id,
              jobs!left(unit_price, customer_id, kanban_job_id, customers!left(id, nickname)),
              kanban_jobs!left(unit_price, customers!left(id, nickname))
            `)
            .order('ship_date', { ascending: false }),
          supabase.from('customers').select('id, nickname').order('nickname'),
        ]);

        if (deliveriesResult.error) throw new Error(`Deliveries fetch error: ${deliveriesResult.error.message}`);
        if (customersResult.error) throw new Error(`Customers fetch error: ${customersResult.error.message}`);

        const deliveriesData = Array.isArray(deliveriesResult.data) ? deliveriesResult.data : [];
        const updatedDeliveries = deliveriesData.map(delivery => {
          // Determine delivery type
          let delivery_type = 'Job';
          if (delivery.kanban_job_id) {
            delivery_type = 'Kanban';
          } else if (delivery.job_id && delivery.jobs?.kanban_job_id) {
            delivery_type = 'WO';
          }

          return {
            ...delivery,
            customer_nickname: delivery.jobs?.customers?.nickname || delivery.kanban_jobs?.customers?.nickname || '-',
            customer_id: delivery.jobs?.customers?.id || delivery.kanban_jobs?.customers?.id || null,
            unit_price: delivery.jobs?.unit_price || delivery.kanban_jobs?.unit_price || 0,
            delivery_type,
          };
        });

        setDeliveries(updatedDeliveries);
        setCustomers(customersResult.data || []);
      } catch (err) {
        console.error('Error fetching deliveries:', err.message);
        toast.error(`Failed to fetch deliveries: ${err.message}`);
        setDeliveries([]);
      } finally {
        setLoading(false);
      }
    };

    fetchDeliveries();
  }, [user]);

  // Close part number dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (partNumberDropdownRef.current && !partNumberDropdownRef.current.contains(event.target)) {
        setShowPartNumberDropdown(false);
      }
    };

    if (showPartNumberDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showPartNumberDropdown]);

  // Get unique part numbers for filter dropdown
  const uniquePartNumbers = useMemo(() => {
    const partNumbers = [...new Set(deliveries.map(d => d.part_number).filter(Boolean))];
    return partNumbers.sort();
  }, [deliveries]);

  // Filter part numbers based on query
  const filteredPartNumbers = useMemo(() => {
    if (!partNumberQuery) return uniquePartNumbers;
    return uniquePartNumbers.filter(pn =>
      pn.toLowerCase().includes(partNumberQuery.toLowerCase())
    );
  }, [uniquePartNumbers, partNumberQuery]);

  // Filter and sort deliveries
  const filteredDeliveries = useMemo(() => {
    let filtered = [...deliveries];

    // Date filter
    const now = new Date();
    const todayStr = formatDateToString(now);

    if (dateFilter === 'last7days') {
      const sevenDaysAgo = new Date(now);
      sevenDaysAgo.setDate(now.getDate() - 7);
      const sevenDaysAgoStr = formatDateToString(sevenDaysAgo);
      filtered = filtered.filter(d => {
        const dateStr = formatDateToString(new Date(d.ship_date));
        return dateStr >= sevenDaysAgoStr && dateStr <= todayStr;
      });
    } else if (dateFilter === 'last30days') {
      const thirtyDaysAgo = new Date(now);
      thirtyDaysAgo.setDate(now.getDate() - 30);
      const thirtyDaysAgoStr = formatDateToString(thirtyDaysAgo);
      filtered = filtered.filter(d => {
        const dateStr = formatDateToString(new Date(d.ship_date));
        return dateStr >= thirtyDaysAgoStr && dateStr <= todayStr;
      });
    } else if (dateFilter === 'last3months') {
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(now.getMonth() - 3);
      const threeMonthsAgoStr = formatDateToString(threeMonthsAgo);
      filtered = filtered.filter(d => {
        const dateStr = formatDateToString(new Date(d.ship_date));
        return dateStr >= threeMonthsAgoStr && dateStr <= todayStr;
      });
    } else if (dateFilter === 'thismonth') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const startOfMonthStr = formatDateToString(startOfMonth);
      filtered = filtered.filter(d => {
        const dateStr = formatDateToString(new Date(d.ship_date));
        return dateStr >= startOfMonthStr && dateStr <= todayStr;
      });
    }

    // Uninvoiced filter
    if (showUninvoiced) {
      filtered = filtered.filter(d => !d.invoice_number);
    }

    // Customer filter
    if (filterCustomer) {
      filtered = filtered.filter(d => d.customer_id === parseInt(filterCustomer));
    }

    // Part number filter
    if (filterPartNumber) {
      filtered = filtered.filter(d => d.part_number === filterPartNumber);
    }

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(d =>
        (d.package_number?.toLowerCase() || '').includes(searchLower) ||
        (d.po_number?.toLowerCase() || '').includes(searchLower)
      );
    }

    return filtered;
  }, [deliveries, dateFilter, showUninvoiced, filterCustomer, filterPartNumber, searchTerm]);

  // Group deliveries by package
  const groupedDeliveries = useMemo(() => {
    const groups = filteredDeliveries.reduce((acc, delivery) => {
      const packageNumber = delivery.package_number;
      if (!acc[packageNumber]) {
        acc[packageNumber] = {
          package_number: packageNumber,
          po_number: delivery.po_number,
          customer_nickname: delivery.customer_nickname,
          total_quantity: 0,
          ship_date: delivery.ship_date,
          unit_price: delivery.unit_price,
          invoice_number: delivery.invoice_number,
          tracking_number: delivery.tracking_number,
          packing_slip_path: delivery.packing_slip_path,
          delivery_type: delivery.delivery_type,
          deliveries: [],
          unique_part_numbers: new Set(),
        };
      }
      acc[packageNumber].total_quantity += delivery.quantity;
      acc[packageNumber].deliveries.push(delivery);
      acc[packageNumber].unique_part_numbers.add(delivery.part_number);
      return acc;
    }, {});

    let result = Object.values(groups).map(group => ({
      ...group,
      unique_part_count: group.unique_part_numbers.size,
      unique_part_numbers: undefined,
    }));

    // Apply sorting
    result.sort((a, b) => {
      let aValue = a[sortField] || '';
      let bValue = b[sortField] || '';

      if (sortField === 'ship_date') {
        const aDate = aValue ? new Date(aValue) : new Date(0);
        const bDate = bValue ? new Date(bValue) : new Date(0);
        return sortDirection === 'asc' ? aDate - bDate : bDate - aDate;
      }

      if (sortField === 'total_quantity') {
        return sortDirection === 'asc' ? (aValue || 0) - (bValue || 0) : (bValue || 0) - (aValue || 0);
      }

      return sortDirection === 'asc'
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });

    return result;
  }, [filteredDeliveries, sortField, sortDirection]);

  // Summary stats
  const summaryStats = useMemo(() => {
    const uninvoicedDeliveries = filteredDeliveries.filter(d => !d.invoice_number);
    const uninvoicedTotal = uninvoicedDeliveries.reduce((sum, d) => sum + (d.quantity * (d.unit_price || 0)), 0);
    const totalPackages = groupedDeliveries.length;
    const totalQuantity = filteredDeliveries.reduce((sum, d) => sum + d.quantity, 0);

    return {
      totalPackages,
      totalQuantity,
      uninvoicedTotal,
      uninvoicedCount: new Set(uninvoicedDeliveries.map(d => d.package_number)).size,
    };
  }, [filteredDeliveries, groupedDeliveries]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  const openEditModal = (delivery) => {
    setSelectedDelivery(delivery);
    setEditInvoiceNumber(delivery.invoice_number || '');
    setEditTrackingNumber(delivery.tracking_number || '');
    setEditShipDate(delivery.ship_date || '');
    setEditNotes(delivery.notes || '');
    setEditQuantity(delivery.quantity || '');
    setInvoiceEntirePackage(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedDelivery(null);
    setEditInvoiceNumber('');
    setEditTrackingNumber('');
    setEditShipDate('');
    setEditNotes('');
    setEditQuantity('');
    setInvoiceEntirePackage(false);
  };

  const openPdfModal = async (packingSlipPath) => {
    try {
      const { data, error } = await supabase.storage
        .from('delivery-files')
        .createSignedUrl(packingSlipPath, 60);
      if (error) throw new Error(`Failed to generate signed URL: ${error.message}`);
      window.open(data.signedUrl, '_blank');
    } catch (err) {
      console.error('Error opening packing slip:', err);
      toast.error(`Failed to open packing slip: ${err.message}`);
    }
  };

  const regeneratePackingSlip = async (packageNumber) => {
    try {
      const { data: packageDeliveries, error: deliveriesError } = await supabase
        .from('deliveries')
        .select('*')
        .eq('package_number', packageNumber);

      if (deliveriesError) throw deliveriesError;

      const jobIds = packageDeliveries.filter(d => d.job_id).map(d => d.job_id);
      const kanbanJobIds = packageDeliveries.filter(d => d.kanban_job_id).map(d => d.kanban_job_id);

      let jobsData = [];
      if (jobIds.length > 0) {
        const { data: jobs } = await supabase
          .from('jobs')
          .select('*, customers(*), parts(*)')
          .in('id', jobIds);
        jobsData = jobsData.concat(jobs || []);
      }

      if (kanbanJobIds.length > 0) {
        const { data: kanbanJobs } = await supabase
          .from('kanban_jobs')
          .select('*, customers(*)')
          .in('id', kanbanJobIds);
        jobsData = jobsData.concat(kanbanJobs || []);
      }

      const pdfBlob = await generatePackingSlipPDF({
        deliveryData: packageDeliveries,
        jobsData: jobsData,
        poNumber: packageDeliveries[0].po_number,
        newDelivery: {
          ship_by: packageDeliveries[0].ship_by,
          tracking_number: packageDeliveries[0].tracking_number,
          notes: packageDeliveries[0].notes
        },
        isKanban: packageDeliveries.some(d => d.kanban_job_id),
        usePartNumberMaster: true
      });

      const fileName = `packing_slip_${packageNumber}_${Date.now()}.pdf`;
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('delivery-files')
        .upload(fileName, pdfBlob, { contentType: 'application/pdf', upsert: false });

      if (uploadError) throw uploadError;

      const { error: updateError } = await supabase
        .from('deliveries')
        .update({ packing_slip_path: uploadData.path })
        .eq('package_number', packageNumber);

      if (updateError) throw updateError;

      const oldPath = packageDeliveries[0].packing_slip_path;
      if (oldPath) {
        await supabase.storage.from('delivery-files').remove([oldPath]);
      }

      return uploadData.path;
    } catch (error) {
      console.error('Error regenerating packing slip:', error);
      throw error;
    }
  };

  const handleUpdateDelivery = async () => {
    if (editQuantity <= 0) {
      toast.error('Quantity must be a positive number.');
      return;
    }
    if (editShipDate && isNaN(new Date(editShipDate).getTime())) {
      toast.error('Please enter a valid ship date.');
      return;
    }

    setIsUpdating(true);

    try {
      if (invoiceEntirePackage) {
        const { error } = await supabase
          .from('deliveries')
          .update({
            invoice_number: editInvoiceNumber || null,
          })
          .eq('package_number', selectedDelivery.package_number);
        if (error) throw new Error(`Failed to update package: ${error.message}`);
      }

      const { error: singleError } = await supabase
        .from('deliveries')
        .update({
          invoice_number: editInvoiceNumber || null,
          tracking_number: editTrackingNumber || null,
          ship_date: editShipDate || null,
          notes: editNotes || null,
          quantity: parseInt(editQuantity),
        })
        .eq('delivery_id', selectedDelivery.delivery_id);
      if (singleError) throw new Error(`Failed to update delivery: ${singleError.message}`);

      const updatedDeliveries = deliveries.map(delivery => {
        if (invoiceEntirePackage && delivery.package_number === selectedDelivery.package_number) {
          return { ...delivery, invoice_number: editInvoiceNumber };
        }
        if (delivery.delivery_id === selectedDelivery.delivery_id) {
          return {
            ...delivery,
            invoice_number: editInvoiceNumber,
            tracking_number: editTrackingNumber,
            ship_date: editShipDate,
            notes: editNotes,
            quantity: parseInt(editQuantity),
          };
        }
        return delivery;
      });

      await regeneratePackingSlip(selectedDelivery.package_number);
      setDeliveries(updatedDeliveries);
      closeModal();
      toast.success('Delivery updated!');
    } catch (err) {
      console.error('Error updating delivery:', err);
      toast.error(`Failed to update: ${err.message}`);
    } finally {
      setIsUpdating(false);
    }
  };

  const handleDeleteDelivery = async () => {
    if (!window.confirm('Delete this entire package? All items will be removed. This cannot be undone.')) return;

    try {
      const { data: packageDeliveries, error: fetchError } = await supabase
        .from('deliveries')
        .select('delivery_id, quantity, job_id, kanban_job_id')
        .eq('package_number', selectedDelivery.package_number);
      if (fetchError) throw new Error(`Failed to fetch package: ${fetchError.message}`);

      for (const delivery of packageDeliveries) {
        if (delivery.job_id) {
          const { data: jobData, error: fetchJobError } = await supabase
            .from('jobs')
            .select('shipped_quantity, quantity_on_order')
            .eq('id', delivery.job_id)
            .single();
          if (fetchJobError) throw new Error(`Failed to fetch job: ${fetchJobError.message}`);

          const newShippedQuantity = Math.max(0, jobData.shipped_quantity - delivery.quantity);
          const newStatus = newShippedQuantity === 0 ? 'ready_for_shipment'
            : newShippedQuantity >= jobData.quantity_on_order ? 'closed' : 'partially_shipped';

          await supabase
            .from('jobs')
            .update({ shipped_quantity: newShippedQuantity, status: newStatus })
            .eq('id', delivery.job_id);
        }

        if (delivery.kanban_job_id) {
          const { data: kanbanData, error: fetchKanbanError } = await supabase
            .from('kanban_jobs')
            .select('shipped_quantity, quantity_on_order')
            .eq('id', delivery.kanban_job_id)
            .single();
          if (fetchKanbanError) throw new Error(`Failed to fetch kanban job: ${fetchKanbanError.message}`);

          const newShippedQuantity = Math.max(0, kanbanData.shipped_quantity - delivery.quantity);
          const newStatus = newShippedQuantity === 0 ? 'ready_for_shipment'
            : newShippedQuantity >= kanbanData.quantity_on_order ? 'closed' : 'partially_shipped';

          await supabase
            .from('kanban_jobs')
            .update({ shipped_quantity: newShippedQuantity, status: newStatus, updated_at: new Date().toISOString() })
            .eq('id', delivery.kanban_job_id);
        }
      }

      await supabase.from('deliveries').delete().eq('package_number', selectedDelivery.package_number);

      if (selectedDelivery.packing_slip_path) {
        await supabase.storage.from('delivery-files').remove([selectedDelivery.packing_slip_path]);
      }

      setDeliveries(prev => prev.filter(d => d.package_number !== selectedDelivery.package_number));
      closeModal();
      toast.success('Package deleted!');
    } catch (err) {
      console.error('Error deleting package:', err);
      toast.error(`Failed to delete: ${err.message}`);
    }
  };

  const handleDeletePackage = async (pkg) => {
    if (!window.confirm('Delete this entire package? All items will be removed. This cannot be undone.')) return;

    try {
      const { data: packageDeliveries, error: fetchError } = await supabase
        .from('deliveries')
        .select('delivery_id, quantity, job_id, kanban_job_id')
        .eq('package_number', pkg.package_number);
      if (fetchError) throw new Error(`Failed to fetch package: ${fetchError.message}`);

      for (const delivery of packageDeliveries) {
        if (delivery.job_id) {
          const { data: jobData, error: fetchJobError } = await supabase
            .from('jobs')
            .select('shipped_quantity, quantity_on_order')
            .eq('id', delivery.job_id)
            .single();
          if (fetchJobError) throw new Error(`Failed to fetch job: ${fetchJobError.message}`);

          const newShippedQuantity = Math.max(0, jobData.shipped_quantity - delivery.quantity);
          const newStatus = newShippedQuantity === 0 ? 'ready_for_shipment'
            : newShippedQuantity >= jobData.quantity_on_order ? 'closed' : 'partially_shipped';

          await supabase
            .from('jobs')
            .update({ shipped_quantity: newShippedQuantity, status: newStatus })
            .eq('id', delivery.job_id);
        }

        if (delivery.kanban_job_id) {
          const { data: kanbanData, error: fetchKanbanError } = await supabase
            .from('kanban_jobs')
            .select('shipped_quantity, quantity_on_order')
            .eq('id', delivery.kanban_job_id)
            .single();
          if (fetchKanbanError) throw new Error(`Failed to fetch kanban job: ${fetchKanbanError.message}`);

          const newShippedQuantity = Math.max(0, kanbanData.shipped_quantity - delivery.quantity);
          const newStatus = newShippedQuantity === 0 ? 'ready_for_shipment'
            : newShippedQuantity >= kanbanData.quantity_on_order ? 'closed' : 'partially_shipped';

          await supabase
            .from('kanban_jobs')
            .update({ shipped_quantity: newShippedQuantity, status: newStatus, updated_at: new Date().toISOString() })
            .eq('id', delivery.kanban_job_id);
        }
      }

      await supabase.from('deliveries').delete().eq('package_number', pkg.package_number);

      if (pkg.packing_slip_path) {
        await supabase.storage.from('delivery-files').remove([pkg.packing_slip_path]);
      }

      setDeliveries(prev => prev.filter(d => d.package_number !== pkg.package_number));
      toast.success('Package deleted!');
    } catch (err) {
      console.error('Error deleting package:', err);
      toast.error(`Failed to delete: ${err.message}`);
    }
  };

  const exportToCSV = () => {
    const headers = ['Package #', 'PO Number', 'Customer', 'Total Qty', 'Unique Parts', 'Ship Date', 'Invoice Status', 'Tracking'];
    const rows = groupedDeliveries.map(g => [
      g.package_number,
      g.po_number,
      g.customer_nickname,
      g.total_quantity,
      g.unique_part_count,
      new Date(g.ship_date).toLocaleDateString(),
      g.invoice_number ? `Invoiced: ${g.invoice_number}` : 'Uninvoiced',
      g.tracking_number || '-'
    ]);

    const csvContent = [headers.join(','), ...rows.map(row => row.map(cell => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'deliveries_export.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-slate-50">
        <p className="text-slate-500">Loading...</p>
      </div>
    );
  }

  return (
    <div className="bg-slate-50 min-h-screen">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 h-12 px-4 flex items-center">
        <div className="flex items-center justify-between gap-4 w-full">
          <div className="flex items-center gap-3">
            <h1 className="text-lg font-semibold text-slate-800">Deliveries</h1>
            <span className="text-xs text-slate-500">{summaryStats.totalPackages} package{summaryStats.totalPackages !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center gap-2">
            {/* Search */}
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search package or PO..."
              className="px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400 w-48"
            />

            {/* Date Filter */}
            <div className="relative">
              <button
                onClick={() => setShowDateDropdown(!showDateDropdown)}
                onBlur={() => setTimeout(() => setShowDateDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[120px] justify-between"
              >
                <span className="text-slate-800">{dateFilterOptions.find(o => o.value === dateFilter)?.label}</span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showDateDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[130px]">
                  {dateFilterOptions.map(option => (
                    <div
                      key={option.value}
                      onClick={() => { setDateFilter(option.value); setShowDateDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${dateFilter === option.value ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {option.label}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Customer Filter */}
            <div className="relative">
              <button
                onClick={() => setShowCustomerDropdown(!showCustomerDropdown)}
                onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 150)}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[130px] justify-between"
              >
                <span className={filterCustomer ? 'text-slate-800' : 'text-slate-500'}>
                  {filterCustomer ? customers.find(c => c.id === parseInt(filterCustomer))?.nickname : 'All Customers'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400" />
              </button>
              {showCustomerDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[140px] max-h-64 overflow-y-auto">
                  <div
                    onClick={() => { setFilterCustomer(''); setShowCustomerDropdown(false); }}
                    className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterCustomer ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                  >
                    All Customers
                  </div>
                  {customers.map(customer => (
                    <div
                      key={customer.id}
                      onClick={() => { setFilterCustomer(String(customer.id)); setShowCustomerDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterCustomer === String(customer.id) ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      {customer.nickname}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Part Number Filter */}
            <div className="relative" ref={partNumberDropdownRef}>
              <button
                onClick={() => { setShowPartNumberDropdown(!showPartNumberDropdown); setPartNumberQuery(''); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors bg-white min-w-[130px] justify-between"
              >
                <span className={filterPartNumber ? 'text-slate-800 truncate max-w-[100px]' : 'text-slate-500'}>
                  {filterPartNumber || 'All Parts'}
                </span>
                <ChevronDownIcon className="w-4 h-4 text-slate-400 flex-shrink-0" />
              </button>
              {showPartNumberDropdown && (
                <div className="absolute z-50 top-full right-0 mt-1 bg-white border border-slate-200 rounded-sm shadow-lg min-w-[200px]">
                  <div className="p-2 border-b border-slate-100">
                    <input
                      type="text"
                      value={partNumberQuery}
                      onChange={(e) => setPartNumberQuery(e.target.value)}
                      placeholder="Search parts..."
                      className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      autoFocus
                      onKeyDown={(e) => {
                        if (e.key === 'Escape') {
                          setShowPartNumberDropdown(false);
                        }
                      }}
                    />
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <div
                      onClick={() => { setFilterPartNumber(''); setShowPartNumberDropdown(false); }}
                      className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${!filterPartNumber ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                    >
                      All Parts
                    </div>
                    {filteredPartNumbers.map(partNumber => (
                      <div
                        key={partNumber}
                        onClick={() => { setFilterPartNumber(partNumber); setShowPartNumberDropdown(false); }}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-slate-50 ${filterPartNumber === partNumber ? 'text-slate-800 font-medium' : 'text-slate-600'}`}
                      >
                        {partNumber}
                      </div>
                    ))}
                    {filteredPartNumbers.length === 0 && partNumberQuery && (
                      <div className="px-3 py-2 text-sm text-slate-400">No matches</div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Uninvoiced Toggle */}
            <button
              onClick={() => setShowUninvoiced(!showUninvoiced)}
              className={`px-3 py-1.5 text-sm rounded-sm transition-colors ${
                showUninvoiced
                  ? 'bg-slate-700 text-white'
                  : 'bg-white text-slate-600 border border-slate-200 hover:bg-slate-50'
              }`}
            >
              Uninvoiced
            </button>

            {/* Export */}
            <button
              onClick={exportToCSV}
              className="px-3 py-1.5 text-sm bg-white text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
            >
              Export
            </button>
          </div>
        </div>
      </div>

      {/* Summary Stats */}
      <div className="px-4 py-3 bg-white border-b border-slate-200">
        <div className="flex items-center gap-6">
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Packages</span>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{summaryStats.totalPackages}</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Total Qty</span>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{summaryStats.totalQuantity.toLocaleString()}</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Uninvoiced</span>
            <p className="text-xl font-semibold text-slate-800 tabular-nums">{summaryStats.uninvoicedCount}</p>
          </div>
          <div className="h-8 w-px bg-slate-200" />
          <div>
            <span className="text-xs text-slate-500 uppercase tracking-wide">Uninvoiced Value</span>
            <p className="text-xl font-semibold text-amber-600 tabular-nums">
              ${summaryStats.uninvoicedTotal.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </p>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border-b border-slate-200">
        <table className="w-full">
          <thead className="sticky top-0 z-10">
            <tr className="bg-slate-50 border-b border-slate-200">
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('package_number')}
              >
                <div className="flex items-center gap-1">
                  Package {sortField === 'package_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('po_number')}
              >
                <div className="flex items-center gap-1">
                  PO # {sortField === 'po_number' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Type</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Customer</th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('total_quantity')}
              >
                <div className="flex items-center gap-1">
                  Qty {sortField === 'total_quantity' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Parts</th>
              <th
                className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide cursor-pointer hover:bg-slate-100 select-none"
                onClick={() => handleSort('ship_date')}
              >
                <div className="flex items-center gap-1">
                  Shipped {sortField === 'ship_date' && (sortDirection === 'asc' ? <ChevronUpIcon className="w-3.5 h-3.5" /> : <ChevronDownIcon className="w-3.5 h-3.5" />)}
                </div>
              </th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Invoice</th>
              <th className="px-3 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide w-32"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {groupedDeliveries.length > 0 ? (
              groupedDeliveries.map((pkg) => (
                <tr key={pkg.package_number} className="hover:bg-slate-50">
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <button
                      onClick={() => { setSelectedPackage(pkg); setIsLineItemModalOpen(true); }}
                      className="text-slate-700 hover:text-slate-900 hover:underline font-medium"
                    >
                      {pkg.package_number}
                    </button>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <Link to={`/jobs/${pkg.po_number}`} className="text-slate-700 hover:text-slate-900 hover:underline">
                      {pkg.po_number}
                    </Link>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    <span className={`inline-flex items-center px-1.5 py-0.5 rounded-sm font-medium ${
                      pkg.delivery_type === 'WO' ? 'bg-purple-100 text-purple-700' :
                      pkg.delivery_type === 'Kanban' ? 'bg-blue-100 text-blue-700' :
                      'bg-slate-100 text-slate-600'
                    }`}>
                      {pkg.delivery_type}
                    </span>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">{pkg.customer_nickname}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-slate-800 tabular-nums">{pkg.total_quantity}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">{pkg.unique_part_count}</td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm text-slate-600">
                    {new Date(pkg.ship_date).toLocaleDateString('en-US', { timeZone: 'UTC' })}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-xs">
                    {pkg.invoice_number ? (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm font-medium bg-green-100 text-green-700">
                        {pkg.invoice_number}
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded-sm font-medium bg-amber-100 text-amber-700">
                        Uninvoiced
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap text-sm">
                    <div className="flex items-center gap-3">
                      <button
                        onClick={() => openEditModal(pkg.deliveries[0])}
                        className="text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                      >
                        Edit
                      </button>
                      {pkg.packing_slip_path && (
                        <button
                          onClick={() => openPdfModal(pkg.packing_slip_path)}
                          className="text-slate-500 hover:text-slate-700 hover:underline transition-colors"
                        >
                          Slip
                        </button>
                      )}
                      <button
                        onClick={() => handleDeletePackage(pkg)}
                        className="text-slate-500 hover:text-red-600 hover:underline transition-colors"
                      >
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="9" className="px-3 py-8 text-center text-slate-400">
                  No deliveries found for the selected filters.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Edit Modal */}
      <Transition appear show={isModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={closeModal}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-sm bg-white p-5 shadow-xl transition-all">
                  <Dialog.Title as="h2" className="text-base font-semibold text-slate-800 mb-4">
                    Edit Delivery
                  </Dialog.Title>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Package #</label>
                      <input
                        type="text"
                        value={selectedDelivery?.package_number || ''}
                        disabled
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm bg-slate-50 text-slate-500"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Quantity</label>
                        <input
                          type="number"
                          value={editQuantity}
                          onChange={(e) => setEditQuantity(e.target.value)}
                          className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                          min="1"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Ship Date</label>
                        <input
                          type="date"
                          value={editShipDate}
                          onChange={(e) => setEditShipDate(e.target.value)}
                          className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Tracking Number</label>
                      <input
                        type="text"
                        value={editTrackingNumber}
                        onChange={(e) => setEditTrackingNumber(e.target.value)}
                        placeholder="Enter tracking number"
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Notes</label>
                      <textarea
                        value={editNotes}
                        onChange={(e) => setEditNotes(e.target.value)}
                        placeholder="Enter notes"
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                        rows="2"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Invoice Number</label>
                      <input
                        type="text"
                        value={editInvoiceNumber}
                        onChange={(e) => setEditInvoiceNumber(e.target.value)}
                        placeholder="Enter invoice number"
                        className="block w-full px-3 py-1.5 text-sm border border-slate-200 rounded-sm focus:outline-none focus:ring-1 focus:ring-slate-400"
                      />
                    </div>
                    <div className="flex items-center">
                      <input
                        type="checkbox"
                        checked={invoiceEntirePackage}
                        onChange={(e) => setInvoiceEntirePackage(e.target.checked)}
                        className="h-4 w-4 text-slate-600 focus:ring-slate-500 border-slate-300 rounded"
                      />
                      <label className="ml-2 text-sm text-slate-600">Invoice Entire Package</label>
                    </div>
                  </div>
                  <div className="flex justify-between items-center mt-5 pt-4 border-t border-slate-200">
                    <button
                      onClick={handleDeleteDelivery}
                      className="inline-flex items-center px-3 py-1.5 text-sm bg-red-600 text-white rounded-sm hover:bg-red-700 transition-colors"
                    >
                      <TrashIcon className="w-4 h-4 mr-1.5" />
                      Delete
                    </button>
                    <div className="flex gap-2">
                      <button
                        onClick={closeModal}
                        className="px-3 py-1.5 text-sm bg-white text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleUpdateDelivery}
                        disabled={isUpdating}
                        className={`inline-flex items-center px-3 py-1.5 text-sm rounded-sm transition-colors ${
                          isUpdating ? 'bg-slate-400 cursor-not-allowed' : 'bg-slate-700 hover:bg-slate-800'
                        } text-white`}
                      >
                        {isUpdating ? (
                          <>
                            <ArrowPathIcon className="w-4 h-4 mr-1.5 animate-spin" />
                            Saving...
                          </>
                        ) : (
                          'Save'
                        )}
                      </button>
                    </div>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>

      {/* Line Item Modal */}
      <Transition appear show={isLineItemModalOpen} as={Fragment}>
        <Dialog as="div" className="relative z-50" onClose={() => setIsLineItemModalOpen(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-slate-900/50" />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-auto">
            <div className="flex min-h-full items-center justify-center p-4">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-200"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-150"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-sm bg-white shadow-xl transition-all">
                  <div className="px-5 py-4 border-b border-slate-200">
                    <Dialog.Title as="h2" className="text-base font-semibold text-slate-800">
                      Package #{selectedPackage?.package_number}
                    </Dialog.Title>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="bg-slate-50 border-b border-slate-200">
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Part Number</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Qty</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Unit Price</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Tracking</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide">Notes</th>
                          <th className="px-4 py-2 text-left text-xs font-semibold text-slate-600 uppercase tracking-wide w-16"></th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {selectedPackage?.deliveries.map((delivery) => (
                          <tr key={delivery.delivery_id} className="hover:bg-slate-50">
                            <td className="px-4 py-2 whitespace-nowrap text-sm font-mono text-slate-700">{delivery.part_number}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm font-medium text-slate-800 tabular-nums">{delivery.quantity}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-600 tabular-nums">
                              {delivery.unit_price ? `$${delivery.unit_price.toFixed(2)}` : '-'}
                            </td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm text-slate-600">{delivery.tracking_number || '-'}</td>
                            <td className="px-4 py-2 text-sm text-slate-600 truncate max-w-xs" title={delivery.notes}>{delivery.notes || '-'}</td>
                            <td className="px-4 py-2 whitespace-nowrap text-sm">
                              <button
                                onClick={() => { setIsLineItemModalOpen(false); openEditModal(delivery); }}
                                className="text-slate-400 hover:text-slate-600 transition-colors"
                                title="Edit"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="px-5 py-3 border-t border-slate-200 flex justify-end">
                    <button
                      onClick={() => setIsLineItemModalOpen(false)}
                      className="px-3 py-1.5 text-sm bg-white text-slate-600 border border-slate-200 rounded-sm hover:bg-slate-50 transition-colors"
                    >
                      Close
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  );
}

export default Deliveries;
